import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useCancelBooking, useCreateBooking, useMyParticipants, useSession, useUpdateBooking } from "../lib/queries";
import { Empty, ErrorBox, Loading } from "../components/Loading";
import { CalendarButton } from "../components/CalendarButton";
import { fmtDayLabel, fmtTimeRange } from "../lib/format";
import { hapticNotification, setBackButton } from "../lib/tg";
import { useToast } from "../components/Toast";

export default function SessionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const sessionQ = useSession(id);
  const kidsQ = useMyParticipants(user?.parent_id ?? undefined);
  const createBooking = useCreateBooking();
  const cancelBooking = useCancelBooking();
  const updateBooking = useUpdateBooking();

  useEffect(() => setBackButton(() => navigate("/")), [navigate]);

  const kids = kidsQ.data ?? [];
  const session = sessionQ.data;

  const bookingByKidId = useMemo(() => {
    const map = new Map<string, { id: string; needs_skipass: boolean }>();
    if (!session) return map;
    for (const b of session.bookings) {
      if (b.status === "cancelled") continue;
      map.set(b.participant_id, { id: b.id, needs_skipass: b.needs_skipass });
    }
    return map;
  }, [session]);

  const taken = session?.bookings.filter((b) => b.status !== "cancelled").length ?? 0;
  const available = session ? Math.max(0, session.capacity - taken) : 0;
  const myBookedCount = kids.filter((k) => bookingByKidId.has(k.id)).length;

  if (sessionQ.isLoading) return <Loading />;
  if (sessionQ.error) return <ErrorBox message={(sessionQ.error as Error).message} />;
  if (!session) return <ErrorBox message="Заняття не знайдено" />;

  const at = session.activity_type;

  async function bookOrCancel(kid: { id: string; name: string }) {
    if (!session) return;
    const existing = bookingByKidId.get(kid.id);
    try {
      if (existing) {
        await cancelBooking.mutateAsync(existing.id);
        hapticNotification("success");
        toast.success(`❎ ${kid.name} скасовано`);
      } else {
        if (available <= 0) {
          toast.error("Місць немає");
          return;
        }
        await createBooking.mutateAsync({
          session_id: session.id,
          participant_id: kid.id,
        });
        hapticNotification("success");
        toast.success(`✅ ${kid.name} записаний`);
      }
    } catch (e) {
      hapticNotification("error");
      toast.error((e as Error).message);
    }
  }

  async function toggleSkipass(bookingId: string, current: boolean) {
    try {
      await updateBooking.mutateAsync({ booking_id: bookingId, needs_skipass: !current });
      hapticNotification("success");
    } catch (e) {
      hapticNotification("error");
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <header>
        <div className="text-tg-hint text-sm">{fmtDayLabel(session.starts_at)}</div>
        <h1 className="text-2xl font-bold mt-1">
          <span className="mr-2">{at.emoji}</span>{at.name}
        </h1>
        <div className="text-tg-hint mt-1">
          {fmtTimeRange(session.starts_at, session.ends_at)} · Тренер: {session.trainer.name}
        </div>
        {session.location_text && <div className="mt-1">📍 {session.location_text}</div>}
        {session.notes && <div className="mt-2 text-sm">{session.notes}</div>}
        <div className="mt-2 text-tg-hint text-sm">{taken}/{session.capacity} записані</div>
      </header>

      {kids.length === 0 ? (
        <Empty label="Спочатку додайте дитину у профіль (Діти)." />
      ) : (
        <section className="card">
          <h3 className="font-semibold mb-2">Записати</h3>
          <p className="text-tg-hint text-xs mb-3">Тапни ім'я — щоб записати або скасувати запис.</p>
          <div className="space-y-2">
            {kids.map((k) => {
              const booking = bookingByKidId.get(k.id);
              const isBooked = !!booking;
              return (
                <div key={k.id} className="flex items-center gap-2 flex-wrap">
                  <button
                    className={`chip flex-1 justify-start ${
                      isBooked ? "bg-green-500/15 text-green-700 border border-green-500/40" : ""
                    }`}
                    onClick={() => bookOrCancel(k)}
                  >
                    {isBooked ? "✓ " : ""}{k.name}
                  </button>
                  {isBooked && at.supports_skipass && (
                    <button
                      className={`chip text-xs ${
                        booking.needs_skipass ? "bg-tg-button text-tg-buttonText" : "text-tg-hint"
                      }`}
                      onClick={() => toggleSkipass(booking.id, booking.needs_skipass)}
                    >
                      🎫 Скіпас
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {available === 0 && myBookedCount === 0 && (
            <div className="text-tg-hint text-sm mt-3">Місць немає.</div>
          )}
        </section>
      )}

      {myBookedCount > 0 && <CalendarButton sessionId={session.id} />}
    </div>
  );
}
